import type { CSSProperties, ReactNode } from "react";
import { theme } from "../ui";

const wizardProgressTrackHeight = 5;

export type WorkflowWizardProgressItem = {
  title: ReactNode;
  /** Wenn true, ist das Segment nicht anklickbar (z. B. noch nicht freigeschalteter Schritt). */
  disabled?: boolean;
};

export type WorkflowWizardProgressProps = {
  /** 0-basierter Index des aktiven Schritts. */
  current: number;
  items: WorkflowWizardProgressItem[];
  onChange?: (step: number) => void;
  style?: CSSProperties;
  showActiveTitle?: boolean;
};

/**
 * Kompakter, mobiltauglicher Ersatz für ein horizontales antd-<Steps> mit vielen
 * langen Labels. Statt alle Titel nebeneinander zu zeigen (die umbrechen, sobald
 * der Platz nicht reicht), erscheint nur der aktive Titel plus eine Positionsleiste:
 * ein Segment pro Schritt, hervorgehoben ist ausschließlich das aktuelle (kein
 * gefüllter Fortschritt bis dahin). Dadurch identische, umbruchfreie Höhe auf
 * Desktop und Handy.
 *
 * Drop-in für die Workflow-Wizards: nimmt dieselben Props (current, items, onChange)
 * wie das bisherige <Steps>. Produktneutral — nur antd-Theme-Tokens, keine Strings.
 */
export function WorkflowWizardProgress({
  current,
  items,
  onChange,
  style,
  showActiveTitle = true,
}: WorkflowWizardProgressProps) {
  const { token } = theme.useToken();
  const total = items.length;
  const safeCurrent = Math.min(Math.max(current, 0), Math.max(total - 1, 0));
  const activeTitle = items[safeCurrent]?.title;

  return (
    <div style={{ ...style }}>
      {showActiveTitle && (
        <div style={{ marginBottom: token.marginXS }}>
          <span style={{ fontSize: token.fontSizeLG, fontWeight: token.fontWeightStrong, color: token.colorText }}>
            {activeTitle}
          </span>
        </div>
      )}
      <div style={{ display: "flex", gap: token.marginXXS }}>
        {items.map((item, index) => {
          const active = index === safeCurrent;
          const clickable = Boolean(onChange) && !item.disabled;
          return (
            <button
              key={index}
              type="button"
              aria-label={typeof item.title === "string" ? item.title : `Step ${index + 1}`}
              aria-current={index === safeCurrent ? "step" : undefined}
              disabled={!clickable}
              onClick={clickable ? () => onChange?.(index) : undefined}
              style={{
                flex: 1,
                height: wizardProgressTrackHeight,
                padding: 0,
                border: "none",
                borderRadius: "var(--mq-pill-radius)",
                background: active ? token.colorPrimary : token.colorBorderSecondary,
                cursor: clickable ? "pointer" : "default",
                transition: "background 0.2s",
              }}
            />
          );
        })}
      </div>
    </div>
  );
}
